import { useCallback, useEffect, useReducer } from 'react';
import {
  AgxAnimatedToggleLoaderState,
  type AgxAnimatedToggleLoaderStateValue,
} from '@urbanx/agx.ui-components2';

export const PDF_GENERATION_TIMEOUT_MS = 30_000;

interface GenerationStatus {
  loaderState?: AgxAnimatedToggleLoaderStateValue;
  isAwaitingCompletion: boolean;
}

type GenerationAction = 'start' | 'fail' | 'complete' | 'timeout' | 'finishAnimation';

const reduceGenerationStatus = (
  status: GenerationStatus,
  action: GenerationAction
): GenerationStatus => {
  switch (action) {
    case 'start':
      return { isAwaitingCompletion: true, loaderState: AgxAnimatedToggleLoaderState.Loading };
    case 'fail':
      return { isAwaitingCompletion: false };
    case 'complete':
      return status.isAwaitingCompletion
        ? { isAwaitingCompletion: false, loaderState: AgxAnimatedToggleLoaderState.Complete }
        : status;
    case 'timeout':
      return { ...status, loaderState: undefined };
    case 'finishAnimation':
      return { ...status, loaderState: undefined };
  }
};

const useBlankContractGenerationStatus = (onStillProcessing: () => void) => {
  const [status, dispatch] = useReducer(reduceGenerationStatus, {
    isAwaitingCompletion: false,
  });
  const start = useCallback(() => dispatch('start'), []);
  const fail = useCallback(() => dispatch('fail'), []);
  const complete = useCallback(() => dispatch('complete'), []);
  const finishAnimation = useCallback(() => dispatch('finishAnimation'), []);

  useEffect(() => {
    if (!status.isAwaitingCompletion || status.loaderState !== AgxAnimatedToggleLoaderState.Loading)
      return;

    const timeout = window.setTimeout(() => {
      dispatch('timeout');
      onStillProcessing();
    }, PDF_GENERATION_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [status.isAwaitingCompletion, status.loaderState, onStillProcessing]);

  return {
    ...status,
    isBlocking: status.loaderState != null,
    start,
    fail,
    complete,
    finishAnimation,
  };
};

export default useBlankContractGenerationStatus;
